'use client';

import type { CSSProperties } from 'react';
import { usePublicUi } from '@/lib/publicUi';

export interface LayoutControlsOverlayProps {
  layoutIndex: number;
  totalLayouts: number;
  layoutLabel?: string;
  onPrev: () => void;
  onNext: () => void;
  /** Theme primary for discs (Containd uses primary @ 72% alpha). */
  accentColor?: string;
  chipBackground?: string;
  chipText?: string;
  chipBorder?: string;
}

const HIT_SIZE = 36;
const VISUAL_SIZE = 24;

function withAlpha(color: string, alpha: number): string {
  const clamped = Math.max(0, Math.min(1, alpha));
  if (color.startsWith('#') && (color.length === 7 || color.length === 4)) {
    let hex = color.slice(1);
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((c) => `${c}${c}`)
        .join('');
    }
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    if (!Number.isNaN(r) && !Number.isNaN(g) && !Number.isNaN(b)) {
      return `rgba(${r}, ${g}, ${b}, ${clamped})`;
    }
  }
  return color;
}

/**
 * Twin of Containd `TemplateLayoutControlsOverlay`:
 * overflow top-left "n/m · name" chip, left/right chevron discs on the edge.
 */
export function LayoutControlsOverlay({
  layoutIndex,
  totalLayouts,
  layoutLabel,
  onPrev,
  onNext,
  accentColor = '#0a7ea4',
  chipBackground = '#ffffff',
  chipText = '#1a1a1a',
  chipBorder = 'rgba(0,0,0,0.18)',
}: LayoutControlsOverlayProps) {
  const { extra } = usePublicUi();
  if (totalLayouts <= 1) return null;

  const label = layoutLabel
    ? `${layoutIndex + 1}/${totalLayouts} · ${layoutLabel}`
    : `${layoutIndex + 1}/${totalLayouts}`;

  const hitBase: CSSProperties = {
    position: 'absolute',
    top: '50%',
    width: HIT_SIZE,
    height: HIT_SIZE,
    marginTop: -HIT_SIZE / 2,
    border: 0,
    padding: 0,
    background: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 4,
  };

  const disc: CSSProperties = {
    width: VISUAL_SIZE,
    height: VISUAL_SIZE,
    borderRadius: VISUAL_SIZE / 2,
    border: `1.5px solid ${withAlpha(chipBorder, 0.85)}`,
    background: withAlpha(accentColor, 0.72),
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  };

  return (
    <div data-layout-controls style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      <div
        style={{
          position: 'absolute',
          top: -24,
          left: 4,
          zIndex: 4,
          pointerEvents: 'none',
          fontSize: 12,
          fontWeight: 700,
          fontFamily: 'Quicksand, system-ui, sans-serif',
          color: chipText,
          background: chipBackground,
          border: `1px solid ${chipBorder}`,
          borderRadius: 4,
          padding: '2px 6px',
          whiteSpace: 'nowrap',
          maxWidth: 'calc(100% - 8px)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
      </div>
      <button
        type="button"
        aria-label={extra.sharePrevLayout}
        style={{ ...hitBase, left: -HIT_SIZE / 2, pointerEvents: 'auto' }}
        onClick={(e) => {
          e.stopPropagation();
          onPrev();
        }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <span style={disc} aria-hidden>
          <ChevronIcon dir="left" />
        </span>
      </button>
      <button
        type="button"
        aria-label={extra.shareNextLayout}
        style={{ ...hitBase, right: -HIT_SIZE / 2, pointerEvents: 'auto' }}
        onClick={(e) => {
          e.stopPropagation();
          onNext();
        }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <span style={disc} aria-hidden>
          <ChevronIcon dir="right" />
        </span>
      </button>
    </div>
  );
}

function ChevronIcon({ dir }: { dir: 'left' | 'right' }) {
  const d =
    dir === 'left'
      ? 'M14 4 L8 10 L14 16'
      : 'M10 4 L16 10 L10 16';
  return (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
      <path
        d={d}
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
